import { Action } from 'typescript-fsa';
import * as dotProp from 'dot-prop-immutable';
import { cloneDeep } from 'lodash';

import { createReducer } from '@topconagriculture/frontend-utils';

import { Concentrate, IngredientPrices, LoadDeliveryData, Milking, MilkPrice } from './dataTools.types';
import {
  addIngredientPrice,
  clearDatatoolsData,
  createConcentrate,
  createMilking,
  createMilkPrice,
  deleteLoadDeliveryBatch,
  deleteMilkPrice,
  getConcentrates,
  getIngredientPrices,
  getLoadsDeliveries,
  getLoadsDeliveriesBatches,
  getMilkings,
  getMilkPrices,
  updateConcentrate,
  updateDelivery,
  updateIngredientPrice,
  updateLoad,
  updateMilking,
  updateMilkPrice,
} from './dataTools.actions';
import { DataToolsType } from './dataTools.constants';

export type DataToolsState = {
  [DataToolsType.LoadsDeliveries]: LoadDeliveryData[];
  [DataToolsType.IngredientPrices]: IngredientPrices[];
  [DataToolsType.MilkProduction]: Milking[];
  [DataToolsType.ExternalConcentrate]: Concentrate[];
  [DataToolsType.MilkPrice]: MilkPrice[];
};

const initialState: DataToolsState = {
  [DataToolsType.LoadsDeliveries]: [],
  [DataToolsType.IngredientPrices]: [],
  [DataToolsType.MilkProduction]: [],
  [DataToolsType.ExternalConcentrate]: [],
  [DataToolsType.MilkPrice]: [],
};

const dataToolsReducer = createReducer(initialState, {
  [getLoadsDeliveriesBatches.done.type]: (state: DataToolsState, action: Action<{ result: any }>) => {
    return dotProp.set(state, DataToolsType.LoadsDeliveries, action.payload.result);
  },
  [getLoadsDeliveries.done.type]: (state: DataToolsState, action: ReturnType<typeof getLoadsDeliveries.done>) => {
    const batches = cloneDeep(state[DataToolsType.LoadsDeliveries]);
    const batchesFetched = action.payload.result.map((data) => ({
      ...data,
      deliveries: data.deliveries
        ? data.deliveries.sort((a, b) => new Date(a.deliveryTime).getTime() - new Date(b.deliveryTime).getTime())
        : [],
      loads: data.loads
        ? data.loads.sort((a, b) => new Date(a.loadTime).getTime() - new Date(b.loadTime).getTime())
        : [],
    }));
    batchesFetched.forEach((batch) => {
      const batchIndex = batches.findIndex((b) => b.id === batch.id);
      if (batchIndex !== -1) {
        batches[batchIndex] = batch;
      }
    });

    return {
      ...state,
      [DataToolsType.LoadsDeliveries]: batches,
    };
  },
  [updateLoad.done.type]: (state: DataToolsState, action: ReturnType<typeof updateLoad.done>) => {
    const { batchId } = action.payload.params;
    const loadUpdated = action.payload.result;
    const batches = cloneDeep(state[DataToolsType.LoadsDeliveries]);
    const batch = batches.find((b) => b.id === batchId);
    if (batch) {
      const loadIndex = batch ? batch.loads.findIndex((load) => load.id === loadUpdated.id) : -1;
      if (loadIndex !== -1) {
        batch.loads[loadIndex] = loadUpdated;
      }
    }

    return {
      ...state,
      [DataToolsType.LoadsDeliveries]: batches,
    };
  },
  [updateDelivery.done.type]: (state: DataToolsState, action: ReturnType<typeof updateDelivery.done>) => {
    const { batchId } = action.payload.params;
    const deliveryUpdated = action.payload.result;
    const batches = cloneDeep(state[DataToolsType.LoadsDeliveries]);
    const batch = batches.find((b) => b.id === batchId);
    if (batch) {
      const deliveryIndex = batch ? batch.deliveries.findIndex((delivery) => delivery.id === deliveryUpdated.id) : -1;
      if (deliveryIndex !== -1) {
        batch.deliveries[deliveryIndex] = deliveryUpdated;
      }
    }

    return {
      ...state,
      [DataToolsType.LoadsDeliveries]: batches,
    };
  },
  [deleteLoadDeliveryBatch.done.type]: (
    state: DataToolsState,
    action: ReturnType<typeof deleteLoadDeliveryBatch.done>,
  ) => {
    const { id } = action.payload.params;
    const batches = state[DataToolsType.LoadsDeliveries].filter((batch) => batch.id !== id);
    return {
      ...state,
      [DataToolsType.LoadsDeliveries]: batches,
    };
  },
  [getIngredientPrices.done.type]: (state: DataToolsState, action: ReturnType<typeof getIngredientPrices.done>) => {
    const data = cloneDeep(state[DataToolsType.IngredientPrices]);
    const { params: ingredientId, result: prices } = action.payload;
    const ingrPrices = data.find((ip) => ip.ingredientId === ingredientId);
    if (ingrPrices) {
      ingrPrices.prices = prices;
    } else {
      data.push({ ingredientId, prices });
    }
    return {
      ...state,
      [DataToolsType.IngredientPrices]: data,
    };
  },
  [updateIngredientPrice.done.type]: (state: DataToolsState, action: ReturnType<typeof updateIngredientPrice.done>) => {
    const { ingredientId, priceId } = action.payload.params;
    const price = action.payload.result;
    return {
      ...state,
      [DataToolsType.IngredientPrices]: state[DataToolsType.IngredientPrices].map((pricesItem) => {
        if (pricesItem.ingredientId !== ingredientId) {
          return pricesItem;
        } else {
          return {
            ...pricesItem,
            prices: pricesItem.prices
              .map((p) => {
                if (p.id !== priceId) {
                  return p;
                } else {
                  return price;
                }
              })
              .sort((p1, p2) => new Date(p2.timeStamp).getTime() - new Date(p1.timeStamp).getTime()),
          };
        }
      }),
    };
  },
  [addIngredientPrice.done.type]: (state: DataToolsState, action: ReturnType<typeof addIngredientPrice.done>) => {
    const { ingredientId } = action.payload.params;
    const price = action.payload.result;
    return {
      ...state,
      [DataToolsType.IngredientPrices]: state[DataToolsType.IngredientPrices].map((pricesItem) => {
        if (pricesItem.ingredientId !== ingredientId) {
          return pricesItem;
        } else {
          return {
            ...pricesItem,
            prices: [...pricesItem.prices, price].sort(
              (p1, p2) => new Date(p2.timeStamp).getTime() - new Date(p1.timeStamp).getTime(),
            ),
          };
        }
      }),
    };
  },
  [getMilkings.done.type]: (state: DataToolsState, action: ReturnType<typeof getMilkings.done>) => {
    return {
      ...state,
      [DataToolsType.MilkProduction]: action.payload.result,
    };
  },
  [updateMilking.done.type]: (state: DataToolsState, action: ReturnType<typeof updateMilking.done>) => {
    const updatedMilking = action.payload.result;
    const { id } = updatedMilking;
    const milkings = state[DataToolsType.MilkProduction].map((milking) =>
      id !== milking.id ? milking : updatedMilking,
    );

    return {
      ...state,
      [DataToolsType.MilkProduction]: milkings,
    };
  },
  [createMilking.done.type]: (state: DataToolsState, action: ReturnType<typeof createMilking.done>) => {
    const milking = action.payload.result;
    const milkings = [...state[DataToolsType.MilkProduction]];
    milkings.push(milking);
    milkings.sort((m1, m2) => new Date(m2.date!).getTime() - new Date(m1.date!).getTime());
    return {
      ...state,
      [DataToolsType.MilkProduction]: milkings,
    };
  },
  [getConcentrates.done.type]: (state: DataToolsState, action: ReturnType<typeof getConcentrates.done>) => {
    return {
      ...state,
      [DataToolsType.ExternalConcentrate]: action.payload.result,
    };
  },
  [updateConcentrate.done.type]: (state: DataToolsState, action: ReturnType<typeof updateConcentrate.done>) => {
    const updatedConcentrate = action.payload.result;
    const { id } = updatedConcentrate;
    const externalconcentrates = state[DataToolsType.ExternalConcentrate].map((concentrate) =>
      id !== concentrate.id ? concentrate : updatedConcentrate,
    );

    return {
      ...state,
      [DataToolsType.ExternalConcentrate]: externalconcentrates,
    };
  },
  [createConcentrate.done.type]: (state: DataToolsState, action: ReturnType<typeof createConcentrate.done>) => {
    const concentrate = action.payload.result;
    const concentrates = [...state[DataToolsType.ExternalConcentrate]];
    concentrates.push(concentrate);
    concentrates.sort((c1, c2) => new Date(c2.date!).getTime() - new Date(c1.date!).getTime());
    return {
      ...state,
      [DataToolsType.ExternalConcentrate]: concentrates,
    };
  },
  [getMilkPrices.done.type]: (state: DataToolsState, action: ReturnType<typeof getMilkPrices.done>) => ({
    ...state,
    [DataToolsType.MilkPrice]: action.payload.result,
  }),
  [createMilkPrice.done.type]: (state: DataToolsState, action: ReturnType<typeof createMilkPrice.done>) => {
    const result = action.payload.result;
    const milkPrices = [...state[DataToolsType.MilkPrice]];
    const newMilkPrices = new Set<MilkPrice>();
    if (result instanceof Array) {
      milkPrices.forEach((milkPrice) => {
        const milk = result.find(
          (newMilkPrice) => newMilkPrice.groupId === milkPrice.groupId && newMilkPrice.date === milkPrice.date,
        );
        if (milk) {
          newMilkPrices.add(milk as any);
        } else {
          newMilkPrices.add(milkPrice as any);
        }
      });
      result.forEach((elem) => newMilkPrices.add(elem));
      return dotProp.set(
        state,
        DataToolsType.MilkPrice,
        Array.from(newMilkPrices).sort((c1, c2) => new Date(c2.date).getTime() - new Date(c1.date).getTime()),
      );
    } else {
      const payload = milkPrices.concat(result);
      payload.sort((c1, c2) => new Date(c2.date).getTime() - new Date(c1.date).getTime());
      return dotProp.set(state, DataToolsType.MilkPrice, payload);
    }
  },
  [updateMilkPrice.done.type]: (state: DataToolsState, action: ReturnType<typeof updateMilkPrice.done>) => {
    const updatedMilkPrice = action.payload.result;
    const indexFound = state.milkprices.findIndex((milkPrice) => milkPrice.id === updatedMilkPrice.id);
    return dotProp.set(state, `${DataToolsType.MilkPrice}.${indexFound}`, updatedMilkPrice);
  },
  [deleteMilkPrice.done.type]: (state: DataToolsState, action: ReturnType<typeof deleteMilkPrice.done>) => {
    const deletedMilkPriceId = action.payload.result;
    const indexFound = state.milkprices.findIndex((milkPrice) => milkPrice.id === deletedMilkPriceId);
    return dotProp.delete(state, `${DataToolsType.MilkPrice}.${indexFound}`);
  },
  [clearDatatoolsData.type]: (state: DataToolsState, action: ReturnType<typeof clearDatatoolsData>) => ({
    ...state,
    [action.payload]: [],
  }),
});

export default dataToolsReducer;
